/**
 * Persistence for the Home screen's device-local state.
 *
 * One preference so far: whether the weather card reads metric. That is a
 * viewer's choice about district data rather than district data itself — the
 * payload carries the same units either way and the toggle never refetches —
 * so it lives in this browser and nowhere else, deliberately not a server
 * field.
 *
 * The module is pure: it imports no Vue and takes its storage as an argument,
 * so it is unit-testable without a browser. Every access is wrapped in
 * try/catch because private mode and a full quota both throw on read and write,
 * and a preference is never worth breaking the page over.
 */

/** The browser storage the module reads and writes; a `Storage` satisfies it.
 *  Injectable so tests can pass a fake. */
export interface PrefsStorage {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
}

export const METRIC_KEY = "home.metric";

/**
 * The browser's storage, or null when it is unavailable. Read lazily and inside
 * a try/catch because merely touching `localStorage` can throw (private mode,
 * a sandboxed frame).
 */
function browserStorage(kind: "local" | "session"): PrefsStorage | null {
	try {
		const storage = kind === "local" ? localStorage : sessionStorage;
		return storage ?? null;
	} catch {
		return null;
	}
}

function read(storage: PrefsStorage | null, key: string): string | null {
	if (!storage) return null;
	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
}

function write(storage: PrefsStorage | null, key: string, value: string): void {
	if (!storage) return;
	try {
		storage.setItem(key, value);
	} catch {
		// Quota reached or storage refused: the choice stays in memory and the
		// page carries on.
	}
}

/**
 * Whether this browser reads the weather card in metric, or null when it has
 * never chosen — null is what lets the district's own preset seed the toggle.
 * Anything other than a stored "true"/"false" reads as never chosen.
 */
export function loadHomeMetric(
	storage: PrefsStorage | null = browserStorage("local"),
): boolean | null {
	const raw = read(storage, METRIC_KEY);
	if (raw === "true") return true;
	if (raw === "false") return false;
	return null;
}

/** Record this browser's choice, which wins over the district seed from then on. */
export function saveHomeMetric(
	metric: boolean,
	storage: PrefsStorage | null = browserStorage("local"),
): void {
	write(storage, METRIC_KEY, metric ? "true" : "false");
}
